import { forkJoin as observableForkJoin } from 'rxjs';
import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { Guid } from '../shared/utils/guid';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { PartnerIdentificationTypeService } from './services/partner-identification-type.service';
import { PartnerType } from './models/partner-type';
import { PartnerIdentificationType } from './models/partner-identification-type';
import { IdentificationType } from './models/identification-type';
import { PartnerComponentBase } from './partner-search-details.base';
import { CurrentUserService } from '../user/services/current-user.service';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoEditingModule, DxiColumnModule, DxoHeaderFilterModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';


@Component({
    templateUrl: './partner-search-details-identifications.component.html',
    selector: 'ep-partner-details-identifications',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxiColumnModule, DxoHeaderFilterModule, DxTemplateModule]
})

export class PartnerSearchDetailsIdentificationComponent extends PartnerComponentBase implements OnInit, AfterViewInit {

    constructor(
        private modalService: GenericModalService,
        private toastService: ToastService,
        private identificationTypeService: PartnerIdentificationTypeService,
        private currentUserService: CurrentUserService) {
        super();
    }

    public partnerIdentifications: PartnerIdentificationType[] = [];
    public identificationTypes: IdentificationType[] = [];
    public partnerTypes: PartnerType[] = [];
    @ViewChild(BizDataGridDirective, { static: true }) gridIdentifications: BizDataGridDirective;

    public allowUpdating = false;
    public allowAdding = false;
    public allowDeleting = false;

    ngOnInit() {
        this.checkPermissions();
    }

    ngAfterViewInit() {
        this.gridIdentifications.instance.option({
            groupPanel: { visible: false },
            searchPanel: { visible: true },
            columnChooser: { enabled: false },
            allowColumnReordering: false
        });

        this.gridIdentifications.instance.option('onEditorPreparing', (e) => {

            if ((e.parentType === 'dataRow' || e.parentType === 'filterRow') && e.dataField === 'IdentificationType') {
                e.editorName = 'dxSelectBox';
                e.editorOptions.dataSource = this.identificationTypes;
                e.editorOptions.displayExpr = 'Name';
                e.editorOptions.value = e.row.data.IdentificationType;
                e.editorOptions.onValueChanged = args => e.setValue(args.value);
                e.editorOptions.itemTemplate = data => {
                    if (data) {
                        const t1 = data.DefaultQualifierEdifact ? `Default Edifact Qualifier: ${data.DefaultQualifierEdifact}` : '';
                        const t2 = data.DefaultQualifierX12 ? `Default X12 Qualifier: ${data.DefaultQualifierX12}` : '';
                        const t3 = t1 ? (t2 ? ` (${t1}; ${t2})` : ` (${t1})`) : (t2 ? ` (${t2})` : ``);
                        return `<div title="${data.Description}${t3}">${data.Name}</div>`;
                    }
                    return undefined;
                };
            }
        });
    }

    checkPermissions() {
        let hasInternalPartnerPermission = this.currentUserService.hasPermission('Partner_Action_InternalPartner');
        if (this.selectedPartner && this.selectedPartner.PartnerType && this.selectedPartner.PartnerType.Name !== 'Internal') {
            // For non-internal partners, ignore the 'internal' permission by always granting it.
            hasInternalPartnerPermission = true;
        }

        this.allowUpdating = hasInternalPartnerPermission && this.currentUserService.hasPermission('Partner_Action_IdentificationEdit');
        this.allowAdding = hasInternalPartnerPermission && this.currentUserService.hasPermission('Partner_Action_IdentificationCreate');
        this.allowDeleting = hasInternalPartnerPermission && this.currentUserService.hasPermission('Partner_Action_IdentificationDelete');
    }

    async partnerSet(): Promise<void> {
        this.checkPermissions();
        const partnerId = this.selectedPartner?.PartnersId;

        const [its, pis] = await Promise.all([
            this.identificationTypeService.getIdentificationTypes(true),
            partnerId ? this.identificationTypeService.getPartnerIdentificationTypes(partnerId) : [],
        ]);

        this.identificationTypes = its.sort((it1, it2) => it1.Name.localeCompare(it2.Name));
        this.partnerIdentifications = pis;

        // Find the correct object references for the identification-types. Otherwise it won't be selected in the grid-dropdown
        this.partnerIdentifications.forEach(pi => pi.IdentificationType =
            this.identificationTypes.find(it => it.IdentificationTypeId === pi.IdentificationType.IdentificationTypeId));
    }

    removeIdentificationRow(e: any) {
        if (e.data.IsMainIdentification) {
            e.cancel = true;
            this.modalService.messageDirect('Delete', 'Default identification can\'t be deleted');
        }
        else {
            if (this.selectedPartner?.EdiConfig?.PartnerIdentificationType?.PartnerIdentificationTypeId === e.data.PartnerIdentificationTypeId) {
                e.cancel = true;
                this.modalService.messageDirect('Delete', 'This identification is used in Edifact Configuration and can\'t be deleted.');
            }
            else if (this.selectedPartner?.X12Config?.PartnerIdentificationType?.PartnerIdentificationTypeId === e.data.PartnerIdentificationTypeId) {
                e.cancel = true;
                this.modalService.messageDirect('Delete', 'This identification is used in X12 Configuration and can\'t be deleted.');
            }
            else {
                const deferred = $.Deferred();
                this.identificationTypeService.deletePartnerIdentificationType(e.data.PartnerIdentificationTypeId).then(() => {
                    deferred.resolve();
                    this.toastService.showToast('Identification deleted...', ToastType.success);
                    super.reloadDetailsData();
                }).catch(() => deferred.reject());
                e.cancel = deferred.promise();
            }
        }
    }

    initNewIdentificationRow(e: any) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        e.data = { newKey: Guid.newGuid(), IsMainIdentification: false, PartnerId: this.selectedPartner.PartnersId };
    }

    handleIdentificationSave(e: any, isInsert: boolean) {
        let partnerIdentificationToSave: PartnerIdentificationType;

        // Find the identification-object.
        if (isInsert) {
            partnerIdentificationToSave = e.data;
        }
        else {
            partnerIdentificationToSave = Object.assign(e.key, e.newData);
        }

        let avoidChangingDefault = false;
        let previousMainIdentification: PartnerIdentificationType = null;

        if (partnerIdentificationToSave.IsMainIdentification === true) {
            // Find previous main-identification if it's different from the one being saved...
            previousMainIdentification = this.partnerIdentifications.find(pi =>
                pi.IsMainIdentification && pi.PartnerIdentificationTypeId !== partnerIdentificationToSave.PartnerIdentificationTypeId);
            if (previousMainIdentification) {
                previousMainIdentification.IsMainIdentification = false;
            }
        }
        else {
            previousMainIdentification = this.partnerIdentifications.find((pi) => pi.IsMainIdentification);
            if (previousMainIdentification == null) {
                avoidChangingDefault = true;
            }
        }

        if (avoidChangingDefault) {
            partnerIdentificationToSave.IsMainIdentification = true;
            e.cancel = true;
            this.gridIdentifications.reset();
            this.modalService.messageDirect('Edit Row', 'Must have a default row.');
        }
        else {
            // This is strange - but the cancel event can be a promise, if any async events are required before saving the changes to the datasource:
            // https://js.devexpress.com/Documentation/16_2/ApiReference/UI_Widgets/dxDataGrid/Configuration/#onRowUpdating
            e.cancel = this.save(partnerIdentificationToSave, previousMainIdentification);
        }
    }

    save(partnerIdentification: PartnerIdentificationType, previousMainIdentification: PartnerIdentificationType) {
        const promises = [];
        promises.push(this.identificationTypeService.savePartnerIdentificationType(partnerIdentification).then((savedIdentification) => {
            Object.assign(partnerIdentification, savedIdentification); // Make sure DB id's are set correctly
            // Set the correct object-reference so it will be chosen correctly in the dropdown...
            partnerIdentification.IdentificationType = this.identificationTypes.find(it => it.IdentificationTypeId === partnerIdentification.IdentificationType.IdentificationTypeId);
            if (partnerIdentification.IsMainIdentification) {
                this.selectedPartner.MainIdentificationType = partnerIdentification;
            }

            // If the main-identification has changed - save both the new/updated identification AND the previous main-identification (to remove the IsMainIdentification flag)
            if (previousMainIdentification) {
                this.identificationTypeService.savePartnerIdentificationType(previousMainIdentification);
            }
        }));

        const deferred = $.Deferred(); // REVIEW: Fix this when Promis/Observalbe is fully supported by DevExpress
        observableForkJoin(promises).subscribe(() => {
            super.refreshSearchData();
            super.reloadDetailsData();
            deferred.resolve();
            this.toastService.showToast('Identification saved...', ToastType.success);
        }, () => deferred.reject());

        return deferred.promise();
    }

    getIdentificationTypeHeaderFilterData = () => this.partnerIdentifications
        .map(e => e.IdentificationType?.Name)
        .filter((val, idx, self) => self.indexOf(val) === idx)
        .map(identificationTypeName => ({
            text: identificationTypeName,
            value: identificationTypeName
        }));

    calculateFilterExpression(filterValue: string, selectedFilterOperation: string) {
        const column = this as object as { displayField: string };
        const operation = selectedFilterOperation ?? 'contains';
        return [column.displayField, operation, filterValue];
    }
}
